import { useState, useEffect } from 'react'
import { useIsOffline } from '@/shared/hooks/use-is-offline'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/shared/hooks/use-auth'
import { queryClient } from '@/shared/lib/tanstack'
import { getJwt } from '@/shared/lib/betterAuth/get-jwt'
import { updateUserAccount } from '@/services/user-account.services'
import { useBanks } from '@/shared/hooks/queries/use-banks'
import { useUserAccount } from '@/shared/hooks/queries/use-user-account'
import { ROUTES } from '@/shared/constants/routes'
import { PAYMENT_QUERY_KEYS } from '@/shared/constants/cache'
import {
  SESSION_STORAGE_KEY,
  SESSION_STORAGE_VALUE,
} from '@/shared/constants/storage'
import { ACCOUNT_TYPE } from '@/shared/types/global.types'
import type { AccountType } from '@/shared/types/global.types'

function isValidRoutingNumber(value: string): boolean {
  return /^\d{9}$/.test(value)
}

function isValidAccountNumber(value: string): boolean {
  return /^\d{5,20}$/.test(value)
}

function isValidAccountHolderName(value: string): boolean {
  return value.trim().length >= 2
}

export function usePaymentOutUpdatePage({
  onSuccess,
}: { onSuccess?: () => void } = {}) {
  const { user, loading } = useAuth()
  const navigate = useNavigate()

  const [accountHolderName, setAccountHolderName] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [routingNumber, setRoutingNumber] = useState('')
  const [bankName, setBankName] = useState('')
  const [accountType, setAccountType] = useState<AccountType>(
    ACCOUNT_TYPE.CHECKING,
  )

  const [touched, setTouched] = useState({
    accountHolderName: false,
    accountNumber: false,
    routingNumber: false,
    bankName: false,
  })
  const [submitted, setSubmitted] = useState(false)

  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | undefined>(undefined)

  const banksQuery = useBanks(!loading && !!user)
  const accountQuery = useUserAccount(!loading && !!user)

  useEffect(() => {
    const data = accountQuery.data
    if (!data) return
    setAccountHolderName(data.accountHolderName ?? '')
    setAccountNumber(data.accountNumber ?? '')
    setRoutingNumber(data.routingNumber ?? '')
    setBankName(data.bankName ?? '')
    setAccountType(data.accountType ?? ACCOUNT_TYPE.CHECKING)
  }, [accountQuery.data])

  function handleBlur(field: keyof typeof touched) {
    setTouched((prev) => ({ ...prev, [field]: true }))
  }

  const showError = (field: keyof typeof touched) => touched[field] || submitted

  const errors = {
    accountHolderName:
      showError('accountHolderName') &&
      !isValidAccountHolderName(accountHolderName)
        ? 'accountHolderNameError'
        : '',
    accountNumber:
      showError('accountNumber') && !isValidAccountNumber(accountNumber)
        ? 'accountNumberError'
        : '',
    routingNumber:
      showError('routingNumber') && !isValidRoutingNumber(routingNumber)
        ? 'routingNumberError'
        : '',
    bankName: showError('bankName') && !bankName.trim() ? 'bankNameError' : '',
  }

  const isFormValid =
    isValidAccountHolderName(accountHolderName) &&
    isValidAccountNumber(accountNumber) &&
    isValidRoutingNumber(routingNumber) &&
    !!bankName.trim()

  async function handleSave() {
    setSubmitted(true)
    if (!isFormValid) return

    setIsSaving(true)
    setSaveError(undefined)
    try {
      const jwt = await getJwt()
      await updateUserAccount(jwt, {
        accountHolderName: accountHolderName.trim(),
        accountNumber,
        routingNumber,
        bankName: bankName.trim(),
        accountType,
      })
      await queryClient.invalidateQueries({ queryKey: PAYMENT_QUERY_KEYS.all })
      sessionStorage.setItem(
        SESSION_STORAGE_KEY.PAYMENT_UPDATED,
        SESSION_STORAGE_VALUE.TRUE,
      )
      if (onSuccess) {
        onSuccess()
      } else {
        navigate({ to: ROUTES.PAYMENT })
      }
    } catch {
      setSaveError('error')
    } finally {
      setIsSaving(false)
    }
  }

  function handleRoutingNumberChange(value: string) {
    setRoutingNumber(value.replace(/\D/g, '').slice(0, 9))
  }

  function handleAccountNumberChange(value: string) {
    setAccountNumber(value.replace(/\D/g, '').slice(0, 20))
  }

  return {
    user,
    isOffline: useIsOffline(),
    accountHolderName,
    setAccountHolderName,
    bankName,
    setBankName,
    accountNumber,
    handleAccountNumberChange,
    routingNumber,
    handleRoutingNumberChange,
    accountType,
    setAccountType,
    accountTypes: [ACCOUNT_TYPE.CHECKING, ACCOUNT_TYPE.SAVING] as AccountType[],
    handleBlur,
    errors,
    banks: banksQuery.data ?? [],
    isLoading: accountQuery.isLoading || banksQuery.isLoading,
    isFormValid,
    isSaving,
    saveError,
    handleSave,
  }
}
